import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Empty, Stat, StageChip } from "../../dashboard/primitives";
import { subscribeAllUsers } from "../../projects/api";
import { subscribeAllRequests } from "../../requests/api";
import { api } from "../../lib/api";

const fmtMoney = (cents, currency = "usd") =>
  (Number(cents || 0) / 100).toLocaleString(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  });

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
};

const OverviewSection = () => {
  const [overview, setOverview] = useState(null);
  const [overviewErr, setOverviewErr] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    let alive = true;
    api
      .get("/api/admin/overview")
      .then(({ data }) => alive && setOverview(data))
      .catch((err) => alive && setOverviewErr(err?.response?.data?.error || err.message));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => subscribeAllUsers(setUsers), []);
  useEffect(() => subscribeAllRequests(setRequests), []);

  const recentUsers = useMemo(() => {
    const toMs = (ts) => (ts?.toDate ? ts.toDate().getTime() : ts ? new Date(ts).getTime() : 0);
    return [...users].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt)).slice(0, 6);
  }, [users]);

  const openRequests = useMemo(() => {
    const closed = new Set(["accepted", "closed", "done", "rejected", "archived"]);
    return requests.filter((r) => !closed.has((r.status || "").toLowerCase())).slice(0, 6);
  }, [requests]);

  const loading = !overview && !overviewErr;
  const rev = overview?.revenue;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card title="Business overview">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 16,
          }}
        >
          <Stat label="Users" value={overview?.users.total ?? "—"} loading={loading} />
          <Stat label="New · 30d" value={overview?.users.newLast30 ?? "—"} loading={loading} />
          <Stat label="Active clients" value={overview?.users.clients ?? "—"} loading={loading} />
          <Stat label="Open requests" value={overview?.requests.open ?? "—"} loading={loading} />
          <Stat label="New leads" value={overview?.leads.new ?? "—"} loading={loading} />
          <Stat
            label="Revenue · 30d"
            value={rev ? fmtMoney(rev.last30Gross, rev.currency) : "—"}
            loading={loading}
          />
          <Stat
            label="Stripe balance"
            value={rev ? fmtMoney(rev.available + rev.pending, rev.currency) : "—"}
            loading={loading}
          />
        </div>
        {overviewErr && (
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--fw-red)", fontFamily: "var(--fw-mono)" }}>
            Metrics unavailable: {overviewErr} — is the API running?
          </div>
        )}
      </Card>

      <div className="fw-stack-md" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <Card
          title="Recent signups"
          action={
            <Link to="/admin/users" style={{ fontFamily: "var(--fw-mono)", fontSize: 11 }}>
              All users →
            </Link>
          }
        >
          {recentUsers.length === 0 ? (
            <Empty>No users yet</Empty>
          ) : (
            recentUsers.map((u) => (
              <div
                key={u.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid var(--fw-line)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{u.displayName || "—"}</div>
                  <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>{u.email}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <StageChip status={u.role || (u.isAdmin ? "admin" : "customer")} />
                  <div style={{ fontSize: 11, color: "var(--fw-muted)", marginTop: 4 }}>
                    {fmtDate(u.createdAt)}
                  </div>
                </div>
              </div>
            ))
          )}
        </Card>

        <Card
          title="Open requests"
          action={
            <Link to="/admin/requests" style={{ fontFamily: "var(--fw-mono)", fontSize: 11 }}>
              All requests →
            </Link>
          }
        >
          {openRequests.length === 0 ? (
            <Empty>Inbox zero — no open requests</Empty>
          ) : (
            openRequests.map((r) => (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid var(--fw-line)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.type || r.title || "Request"}</div>
                  <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>
                    {r.scope || r.notes || "—"}
                  </div>
                </div>
                <StageChip status={r.status || "new"} />
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
};

export default OverviewSection;
